import { useCallback, useRef } from 'react';
import { StudiesAPI } from '@/state/slices/studies';
import { MediaAPI } from '@/state/slices/media';

/**
 * Returns a prefetch function that eagerly loads study data, media, and
 * thumbnails into the RTK Query cache + browser HTTP cache.
 *
 * Call it on `onMouseEnter` of any element that navigates to a study.
 * Duplicate calls for the same study UUID are no-ops.
 */
export function useStudyPrefetch() {
  const prefetchedRef = useRef<Set<string>>(new Set());

  const prefetch = useCallback(async (studyUuid: string) => {
    if (!studyUuid || prefetchedRef.current.has(studyUuid)) return;
    prefetchedRef.current.add(studyUuid);

    // Lazy import to avoid module-level store initialization in test environments
    const { store } = await import('@/state/store');

    // Prefetch study and media data in parallel
    const [studyResult, mediaResult] = await Promise.all([
      store.dispatch(
        StudiesAPI.endpoints.getStudy.initiate({ uuid: studyUuid })
      ),
      store.dispatch(
        MediaAPI.endpoints.getMedia.initiate({
          study_uuid: studyUuid,
          paginate: false,
        })
      ),
    ]);

    // Prefetch DICOM thumbnails into browser cache
    if (studyResult.data?.dicoms) {
      studyResult.data.dicoms.forEach((dicom) => {
        if (!dicom.thumbnail) return;
        fetch(dicom.thumbnail, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          priority: 'low',
        }).catch(() => {});
      });
    }

    // Prefetch media files into browser cache
    if (mediaResult.data?.results) {
      mediaResult.data.results.forEach((media) => {
        if (!media.file) return;
        fetch(media.file, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          priority: 'low',
        }).catch(() => {});
      });
    }
  }, []);

  return prefetch;
}
